import { lazy } from "react";
import { Outlet, Route } from "react-router";
import { Stack, Typography } from "@mui/material";
import { FoodsIllustrations, WhiteBox } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { SettingsProvider } from "src/context/Settings/GlobalSettings/provider";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { legacySettingsRedirectLoader } from "./legacySettingsRedirects";
import { configurationRoutes } from "./router/configurationRoutes";
import { contentRoutes } from "./router/contentRoutes";
import { legacyRoutes } from "./router/legacyRoutes";
import { organizationRoutes } from "./router/organizationRoutes";
import { referenceRoutes } from "./router/referenceRoutes";
import { SettingsRouteBoundary } from "./router/SettingsRouteBoundary";
import { SettingsInitialRedirection } from "./SettingsInitialRedirection";
import { canAccessSettings } from "./settingsAccessPermissions";
import { RouteErrorPage } from "src/components/PageState/RouteErrorPage";

const SettingsLayoutLazy = lazy(() => import("./SettingsLayout"));

export const useSettingsRouter = () => (
    <Route
        id="settings"
        element={<SettingsGlobalContext />}
        errorElement={<RouteErrorPage />}
        loader={legacySettingsRedirectLoader}
    >
        <Route
            path="settings"
            element={
                <SettingsRouteBoundary>
                    <SettingsLayoutLazy />
                </SettingsRouteBoundary>
            }
        >
            <Route index element={<SettingsInitialRedirection />} />
            {configurationRoutes()}
            {contentRoutes()}
            {referenceRoutes()}
            {organizationRoutes()}
            {legacyRoutes()}
        </Route>
    </Route>
);

const SettingsGlobalContext = () => {
    const { canUser } = useFoodpilotContext();
    const { t } = useTranslation();
    const canSeeSettings = canAccessSettings(canUser);

    if (canSeeSettings === false) {
        return (
            <WhiteBox margin={3}>
                <Stack gap="32px" alignItems="center" padding="32px">
                    <FoodsIllustrations illustration="underConstruction" />
                    <Typography variant="h3">
                        {t("settings.cannot_access", "You're not allowed to see the Settings")}
                    </Typography>
                </Stack>
            </WhiteBox>
        );
    }

    return (
        <SettingsProvider>
            <Outlet />
        </SettingsProvider>
    );
};
